import React from 'react';
import { Box, Typography } from '@mui/material';
import { motion } from 'framer-motion';
import Map_configure from '../../../Images/Map_configure.png';
import Connect_source from '../../../Images/Connect_source.png';
import Deploy_monitor from '../../../Images/Deploy_monitor.png';

const Works = () => {
  return (
    <Box
      sx={{
        width: '100%',
        py: { xs: 6, md: 10 },
        px: { xs: 2, md: 4 },
        backgroundColor: '#EDF5FE',
        minHeight: '70vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Header Section */}
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
      >
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '2rem', sm: '2.5rem', md: '3.0rem' },
            color: '#383838',
            mb: 2,
            textAlign: 'center',
            letterSpacing: '-0.02em',
          }}
        >
          How it works?
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: '#383838',
            fontSize: { xs: '1rem', md: '1rem' },
            fontWeight: 400,
            textAlign: 'center',
            maxWidth: '800px',
            mx: 'auto',
            mb: 6,
            lineHeight: 1.6,
          }}
        >
          SkyShift streamlines your data integration through a seamless ETL
          process.
          <br />
          Experience efficiency and accuracy like never before.
        </Typography>
      </motion.div>

      {/* Steps Row */}
      <Box
        sx={{
          width: '100%',
          maxWidth: '1400px',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'flex-start',
          justifyContent: 'space-around',
          gap: { xs: 6, md: 0 },
          zIndex: 2,
        }}
      >
        {/* Step 1: Connect Source */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          whileHover={{
            scale: 1.05,
            transition: { duration: 0.3 },
          }}
          style={{ flex: 1, display: 'flex', justifyContent: 'center' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              maxWidth: '320px',
              position: 'relative',
              background: 'none',
              boxShadow: 'none',
            }}
          >
            <Box
              sx={{
                width: '380px',
                height: '200px',
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'none',
              }}
            >
              <img
                src={Connect_source}
                alt="Connect Source"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 500,
                fontSize: { xs: '1.5rem', md: '1.7rem' },
                color: '#062C83',
                mb: 2,
                letterSpacing: '-0.02em',
              }}
            >
              Connect Source
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#383838',
                fontSize: { xs: '0.9rem', md: '1rem' },
                lineHeight: 1.6,
                fontWeight: 400,
                maxWidth: '260px',
              }}
            >
              Connect your data sources — from cloud apps and databases to CRMs
              — in just a few clicks. Simply plug in your credentials to get
              started.
            </Typography>
          </Box>
        </motion.div>

        {/* Step 2: Map & Configure */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          whileHover={{
            scale: 1.05,
            transition: { duration: 0.3 },
          }}
          style={{ flex: 1, display: 'flex', justifyContent: 'center' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              maxWidth: '320px',
              position: 'relative',
              background: 'none',
              boxShadow: 'none',
            }}
          >
            <Box
              sx={{
                width: '180px',
                height: '180px',
                mb: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'none',
              }}
            >
              <img
                src={Map_configure}
                alt="Map & Configure"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 500,
                fontSize: { xs: '1.5rem', md: '1.7rem' },
                color: '#062C83',
                mb: 2,
                letterSpacing: '-0.02em',
              }}
            >
              Map & Configure
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#383838',
                fontSize: { xs: '0.9rem', md: '1rem' },
                lineHeight: 1.6,
                fontWeight: 400,
                maxWidth: '260px',
              }}
            >
              Use the no-code visual builder to map fields, apply transformation
              rules, and clean your data with built-in validation.
            </Typography>
          </Box>
        </motion.div>

        {/* Step 3: Deploy & Monitor */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          whileHover={{
            scale: 1.05,
            transition: { duration: 0.3 },
          }}
          style={{ flex: 1, display: 'flex', justifyContent: 'center' }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              maxWidth: '320px',
              position: 'relative',
              background: 'none',
              boxShadow: 'none',
            }}
          >
            <Box
              sx={{
                width: '280px',
                height: '180px',
                mb: 4,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'none',
              }}
            >
              <img
                src={Deploy_monitor}
                alt="Deploy & Monitor"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                }}
              />
            </Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 500,
                fontSize: { xs: '1.5rem', md: '1.7rem' },
                color: '#062C83',
                mb: 2,
                letterSpacing: '-0.02em',
              }}
            >
              Deploy & Monitor
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#383838',
                fontSize: { xs: '0.9rem', md: '1rem' },
                lineHeight: 1.6,
                fontWeight: 400,
                maxWidth: '260px',
              }}
            >
              Track pipeline health, get alerts on failures, and review sync
              history to ensure consistent and reliable data delivery.
            </Typography>
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
};

export default Works;
